import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Calendar, MapPin, Music, ArrowLeft, Phone } from "lucide-react";
import { getEventById } from "../data/eventsData";
import { useI18n } from "../data/i18n";
import { restaurantInfo } from "../data/mock";
import Reveal from "../components/Reveal";

const DiamondDivider = () => (
  <div className="flex items-center justify-center gap-2 my-4">
    <div className="w-12 h-px bg-gradient-to-r from-transparent to-[#e4c590]"></div>
    <span className="diamond-mark"></span>
    <span className="diamond-mark opacity-60"></span>
    <span className="diamond-mark"></span>
    <div className="w-12 h-px bg-gradient-to-l from-transparent to-[#e4c590]"></div>
  </div>
);

const EventDetail = () => {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const event = getEventById(id);

  if (!event) {
    return (
      <div className="bg-[#0a0a0a] pt-32 pb-24 min-h-screen text-center">
        <p className="text-[#cfc7b8] mb-6">Event not found</p>
        <Link to="/events" className="cb-btn cb-btn-outline inline-block">
          <span className="cb-btn-text cb-btn-text-1">{t("events.back")}</span>
          <span className="cb-btn-text cb-btn-text-2" aria-hidden="true">{t("events.back")}</span>
        </Link>
      </div>
    );
  }

  // Check if this is a "no music/theme" event (IDs 7, 8, 9, 10)
  const noMusicTheme = ["7", "8", "9", "10"].includes(String(event.id));

  const title = lang === "bg" ? event.titleBg : event.titleEn;
  const date = lang === "bg" ? event.dateBg : event.dateEn;
  const place = lang === "bg" ? event.place : (event.placeEn || event.place);
  const music = lang === "bg" ? event.musicBg : event.musicEn;
  const includes = lang === "bg" ? event.includesBg : event.includesEn;
  const extra = lang === "bg" ? event.extraBg : event.extraEn;
  const programme = lang === "bg" ? (event.programBg || []) : (event.programEn || []);
  const price = lang === "bg" ? event.priceBg : event.priceEn;
  const priceLabel = lang === "bg" ? event.priceLabelBg : event.priceLabelEn;
  const warningTitle = lang === "bg" ? event.warningTitleBg : event.warningTitleEn;
  const warningBody = lang === "bg" ? event.warningBodyBg : event.warningBodyEn;

  return (
    <div className="bg-[#0a0a0a] min-h-screen pb-24">
      {/* Hero */}
      <section
        className="relative pt-40 pb-24 lg:pt-48 lg:pb-32 overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(10,10,10,0.65), rgba(10,10,10,0.92)), url(${event.detailImage || event.image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-5xl mx-auto px-6 text-center relative z-10">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-[#cfc7b8] hover:text-[#e4c590] transition-colors mb-8 text-xs uppercase tracking-widest">
            <ArrowLeft size={14} />
            <span>{t("events.back")}</span>
          </button>

          <Reveal direction="up">
            <p className="section-label">{t("events.label")}</p>
            <h1 className="display text-5xl md:text-7xl lg:text-8xl text-[#f5f0e6] mt-3 mb-2 leading-tight">{title}</h1>
            <DiamondDivider />
            <p className="text-[#cfc7b8] text-lg italic">{date}</p>
          </Reveal>
        </div>
      </section>

      {/* Info cards */}
      <section className="max-w-6xl mx-auto px-6 -mt-16 relative z-20">
        <Reveal direction="up">
          {/* Dynamic grid - 3 columns normally, 2 columns when no music theme */}
          <div className={`grid grid-cols-1 ${noMusicTheme ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-4 max-w-${noMusicTheme ? '4xl' : '6xl'} mx-auto`}>
            <div className="bg-[#0f0f0f] border border-[#1f1f1f] p-8 text-center">
              <Calendar size={28} className="text-[#e4c590] mx-auto mb-4" />
              <p className="text-[#e4c590] uppercase tracking-widest text-xs mb-2">{t("events.date_time")}</p>
              <p className="text-[#f5f0e6]">{date}</p>
            </div>
            <div className="bg-[#0f0f0f] border border-[#1f1f1f] p-8 text-center">
              <MapPin size={28} className="text-[#e4c590] mx-auto mb-4" />
              <p className="text-[#e4c590] uppercase tracking-widest text-xs mb-2">{t("events.place")}</p>
              <p className="text-[#f5f0e6]">{place}</p>
            </div>
            {/* Music/Theme card - CONDITIONALLY RENDERED */}
            {!noMusicTheme && (
              <div className="bg-[#0f0f0f] border border-[#1f1f1f] p-8 text-center">
                <Music size={28} className="text-[#e4c590] mx-auto mb-4" />
                <p className="text-[#e4c590] uppercase tracking-widest text-xs mb-2">{t("events.music_theme")}</p>
                <p className="text-[#f5f0e6]">{music}</p>
              </div>
            )}
          </div>
        </Reveal>

        {/* Price banner */}
        {price && (
          <Reveal direction="up" delay={150}>
            <div className="mt-4 bg-gradient-to-r from-[#0f0f0f] via-[#1a1208] to-[#0f0f0f] border border-[#e4c590]/40 px-8 py-6 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-center">
              <p className="text-[#e4c590] uppercase tracking-[0.3em] text-xs">{priceLabel}</p>
              <p className="display text-3xl md:text-4xl text-[#f5f0e6]">{price}</p>
            </div>
          </Reveal>
        )}

        {/* Limited seats warning */}
        {warningTitle && (
          <Reveal direction="up" delay={250}>
            <div className="mt-4 border-2 border-[#e4c590] bg-[#0a0a0a] px-8 py-6 text-center">
              <p className="display text-2xl md:text-3xl text-[#e4c590] mb-2 tracking-wider">{warningTitle}</p>
              <p className="text-[#cfc7b8] text-sm md:text-base">{warningBody}</p>
            </div>
          </Reveal>
        )}
      </section>

      {/* Programme */}
      {programme.length > 0 && (
        <section className="py-24 lg:py-32">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <Reveal direction="up">
              <p className="section-label">{lang === "bg" ? "Програма" : "Programme"}</p>
              <h2 className="display text-4xl md:text-5xl text-[#f5f0e6] mt-3">{t("events.program")}</h2>
              <DiamondDivider />
            </Reveal>

            <div className="mt-12 space-y-4">
              {programme.map((line, idx) => (
                <Reveal key={idx} direction="up" delay={idx * 90}>
                  <div className="bg-[#0f0f0f] border border-[#1f1f1f] px-6 py-4 text-[#cfc7b8] hover:border-[#e4c590]/60 transition-colors">
                    {line}
                  </div>
                </Reveal>
              ))}
            </div>

            {includes && (
              <p className="text-[#7a7468] text-sm mt-8 italic">* {includes}</p>
            )}
            {extra && (
              <p className="text-[#cfc7b8] text-sm mt-4">{extra}</p>
            )}
          </div>
        </section>
      )}

      {/* Special menu — with blurred backdrop image */}
      {event.menu && event.menu.length > 0 && (
        <section className="relative py-24 overflow-hidden">
          <div
            className="absolute inset-0 -z-0"
            style={{
              backgroundImage: `url(${event.detailImage || event.image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "blur(14px) brightness(0.35)",
              transform: "scale(1.08)",
            }}
          ></div>
          <div className="absolute inset-0 bg-[#0a0a0a]/75 -z-0"></div>

          <div className="relative max-w-5xl mx-auto px-6 z-10">
            <Reveal direction="up">
              <div className="text-center mb-16">
                <p className="section-label">CASA BALA</p>
                <h2 className="display text-4xl md:text-5xl text-[#f5f0e6] mt-3">{t("events.special_menu")}</h2>
                <DiamondDivider />
              </div>
            </Reveal>

            <div className="space-y-6">
              {event.menu.map((dish, idx) => {
                const name = lang === "bg" ? dish.nameBg : dish.nameEn;
                const desc = lang === "bg" ? dish.descBg : dish.descEn;
                const section = lang === "bg" ? dish.sectionBg : dish.sectionEn;
                const prevSection = idx > 0 ? (lang === "bg" ? event.menu[idx - 1].sectionBg : event.menu[idx - 1].sectionEn) : null;
                const showSection = section && section !== prevSection;
                return (
                  <Reveal key={idx} direction={idx % 2 === 0 ? "left" : "right"} delay={idx * 80}>
                    <div>
                      {showSection && (
                        <p className="text-[#e4c590] uppercase tracking-[0.3em] text-xs mb-3 mt-6 first:mt-0">{section}</p>
                      )}
                      <div className="grid grid-cols-[auto_1fr] gap-6 items-start border-b border-[#1f1f1f] pb-6">
                        <div className="w-12 h-12 border border-[#e4c590]/40 rounded-full flex items-center justify-center text-[#e4c590] text-sm display">
                          {String(idx + 1).padStart(2, "0")}
                        </div>
                        <div>
                          <h3 className="display text-2xl text-[#f5f0e6] mb-2">{name}</h3>
                          <p className="text-[#a8a297] text-sm leading-relaxed">{desc}</p>
                        </div>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <Reveal direction="up">
            {price && (
              <p className="text-[#e4c590] mb-4 text-lg">{price}</p>
            )}
            <h2 className="display text-3xl md:text-4xl text-[#f5f0e6] mb-6">{t("events.reservations")}</h2>
            <p className="text-[#cfc7b8] mb-8">{t("res.only_phone")}</p>
            <a href={`tel:${restaurantInfo.phone}`} className="cb-btn cb-btn-gold inline-flex items-center gap-2">
              <span className="cb-btn-text cb-btn-text-1 inline-flex items-center gap-2"><Phone size={14} /> {restaurantInfo.phone}</span>
              <span className="cb-btn-text cb-btn-text-2 inline-flex items-center gap-2" aria-hidden="true"><Phone size={14} /> {restaurantInfo.phone}</span>
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default EventDetail;